import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, ArrowRight } from 'lucide-react';
import { Button } from '../components/Button';
import { RatingSlider } from '../components/RatingSlider';
import { ProtectedLayout } from '../components/ProtectedLayout';
import { PillarRating, PILLARS } from '../types/alignment';
import { supabase } from '../lib/supabase';

export function AlignmentAuditStart() {
  const navigate = useNavigate();
  const [ratings, setRatings] = useState<{
    mindset_rating?: PillarRating;
    outreach_rating?: PillarRating;
    delivery_rating?: PillarRating;
    ops_rating?: PillarRating;
  }>({});

  const updateRating = (key: string, value: PillarRating) => {
    setRatings(prev => ({ ...prev, [key]: value }));
  };

  const allRatingsComplete = ratings.mindset_rating && ratings.outreach_rating &&
    ratings.delivery_rating && ratings.ops_rating;

  const handleNext = async () => {
    if (!allRatingsComplete) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/');
      return;
    }

    const { data: audit, error } = await supabase
      .from('alignment_audits')
      .insert({
        user_id: user.id,
        mindset_rating: ratings.mindset_rating,
        outreach_rating: ratings.outreach_rating,
        delivery_rating: ratings.delivery_rating,
        ops_rating: ratings.ops_rating,
        selected_area: ''
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating audit:', error);
      return;
    }

    navigate('/alignment-audit/select-area', {
      state: { auditId: audit.id, ratings }
    });
  };

  return (
    <ProtectedLayout>
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-600 rounded-full mb-4">
              <Target className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Alignment Audit
            </h1>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Certainty starts with data. Rate your current level of certainty in each area of your business to identify where alignment is needed most.
            </p>
          </div>

          <div className="space-y-6 mb-8">
            {PILLARS.map((pillar) => (
              <RatingSlider
                key={pillar.id}
                label={pillar.question}
                description={pillar.description}
                value={ratings[pillar.ratingKey]}
                onChange={(value) => updateRating(pillar.ratingKey, value)}
              />
            ))}
          </div>

          <div className="flex justify-center">
            <Button
              onClick={handleNext}
              disabled={!allRatingsComplete}
              className="px-8 py-4 text-lg"
            >
              Next: Select Area to Align
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </ProtectedLayout>
  );
}
